import React from "react";
import { Link } from "react-router-dom";
import { useI18n } from "@/contexts/I18nContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MapPin, BookOpen } from "lucide-react";
import { BEACH_GUIDES } from "@/data/beachGuide";

export default function BeachSelector({ beaches, beachId, onChange }) {
  const { t } = useI18n();
  const current = beaches?.find((b) => b.id === beachId);
  // If the currently-selected beach has a dedicated detail page, expose a
  // small "See surf guide" link so visitors can dive deeper (and Google can
  // crawl it).
  const guide = current ? BEACH_GUIDES[current.id] : null;
  return (
    <div data-testid="beach-selector" className="flex items-center gap-3 flex-wrap">
      <div className="flex items-center gap-2 text-sm text-[color:var(--ink-2)]">
        <MapPin className="w-4 h-4 text-[color:var(--coral)]" />
        <span className="eyebrow">{t("beach")}</span>
      </div>
      <Select value={beachId} onValueChange={onChange}>
        <SelectTrigger
          data-testid="beach-select-trigger"
          className="w-[260px] rounded-2xl bg-white border-[color:var(--hairline)] font-semibold text-[color:var(--ink)] h-11"
        >
          <SelectValue placeholder={t("select_beach")} />
        </SelectTrigger>
        <SelectContent className="rounded-2xl">
          {beaches?.map((b) => (
            <SelectItem
              key={b.id}
              value={b.id}
              data-testid={`beach-option-${b.id}`}
            >
              <div className="flex flex-col">
                <span className="font-medium">{b.name}</span>
                <span className="text-xs text-[color:var(--ink-2)]">{b.region}</span>
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {current && (
        <p data-testid="beach-blurb" className="text-sm text-[color:var(--ink-2)] italic max-w-md">
          {current.blurb}
        </p>
      )}
      {guide && (
        <Link
          to={`/surf/${guide.slug}`}
          data-testid={`beach-guide-link-${guide.slug}`}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-[color:var(--coral)] hover:text-[color:var(--coral)]/80"
        >
          <BookOpen className="w-3.5 h-3.5" /> Full surf guide →
        </Link>
      )}
    </div>
  );
}
